import { type FormEvent, useEffect, useMemo, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { Building2, CalendarDays, FileCheck2, IndianRupee, Landmark, ShieldCheck, UserRoundCog } from 'lucide-react'

import { useAuth } from '@/contexts/AuthContext'
import { normalizeRole } from '@/security/accessContract'
import { getBusinessPortalService } from '@/services/businessPortal'
import type {
  BusinessAccessContext,
  BusinessPortalRole,
  BusinessWorkspace,
  ComplianceItem,
  RepresentativeGrant,
} from '@/types/businessPortal.types'

const EMPTY_WORKSPACE: BusinessWorkspace = {
  gst: [], incomeTax: [], tds: [], notices: [], documents: [], deadlines: [], payments: [],
}

const DEFAULT_ACTIONS = [
  'txh.client.read', 'txh.document.read', 'txh.document.upload',
  'txh.gst_assessment.read', 'txh.gst_assessment.summary',
  'txh.assessment.read', 'txh.reminder.read', 'txh.payment.read',
]

function pretty(value: string): string {
  return value.replace(/^txh\./, '').replace(/_/g, ' ').replace(/\./g, ' / ')
}

function currency(value: number, code = 'INR'): string {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: code, maximumFractionDigits: 0 }).format(value)
}

function Section({ title, hint, items, unavailable = false }: { title: string; hint: string; items: ComplianceItem[]; unavailable?: boolean }) {
  return (
    <section className="card card-elevated p-5 space-y-4">
      <div>
        <h2 className="text-lg font-bold text-text-primary">{title}</h2>
        <p className="text-sm text-text-secondary">{hint}</p>
      </div>
      {unavailable ? (
        <div role="status" className="rounded-xl border border-dashed border-border p-4 text-sm text-text-secondary">GST information is unavailable. Published GST files may be available in your client vault.</div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-4 text-sm text-text-secondary">No scoped items available.</div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <article key={item.id} className="rounded-xl border border-border bg-surface-light/60 p-4 dark:bg-surface-dark/60">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-semibold text-text-primary">{item.title}</h3>
                  {item.subtitle && <p className="text-sm text-text-secondary">{item.subtitle}</p>}
                </div>
                <span className="rounded-full bg-primary-50 px-3 py-1 text-xs font-semibold text-primary-700 dark:bg-primary-900/30 dark:text-primary-200">{pretty(item.status)}</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-4 text-sm text-text-secondary">
                {item.dueDate && <span>Due {new Date(item.dueDate).toLocaleDateString('en-IN')}</span>}
                {item.amount !== undefined && <span>{currency(item.amount, item.currency)}</span>}
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  )
}

export default function BusinessPortalPage() {
  const { user } = useAuth()
  const authorityKey = JSON.stringify([
    user?.id,
    user?.currentSessionId,
    user?.authScope,
    user?.tenantId,
    user?.role,
    [...(user?.permissions ?? [])].sort(),
  ])
  return <BusinessPortalWorkspace key={authorityKey} />
}

function BusinessPortalWorkspace() {
  const { user } = useAuth()
  const service = useMemo(() => getBusinessPortalService(), [])
  const normalizedRole = normalizeRole(user?.role || '')
  const role = normalizedRole === 'business_owner_client' || normalizedRole === 'authorized_client'
    ? normalizedRole as BusinessPortalRole : null
  const [context, setContext] = useState<BusinessAccessContext | null>(null)
  const [grants, setGrants] = useState<RepresentativeGrant[]>([])
  const [selectedId, setSelectedId] = useState('')
  const [loadedWorkspace, setWorkspace] = useState<BusinessWorkspace>(EMPTY_WORKSPACE)
  const [workspaceOwner, setWorkspaceOwner] = useState('')
  const [loading, setLoading] = useState(true)
  const [workspaceLoading, setWorkspaceLoading] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [grantee, setGrantee] = useState('')
  const [evidence, setEvidence] = useState('')
  const [expiry, setExpiry] = useState('')
  const workspace = workspaceOwner === selectedId ? loadedWorkspace : EMPTY_WORKSPACE
  const visibleWorkspaceLoading = workspaceLoading || (!!selectedId && workspaceOwner !== selectedId)

  async function refresh() {
    if (!role) return
    const [nextContext, nextGrants] = await Promise.all([
      service.getAccessContext(role), service.listGrants(),
    ])
    setContext(nextContext)
    setGrants(nextGrants)
    setSelectedId((current) => current && nextContext.entities.some((item) => item.id === current)
      ? current : nextContext.entities[0]?.id || '')
  }

  useEffect(() => {
    if (!role) return
    setLoading(true)
    refresh().catch(() => setError('The business workspace could not be loaded securely.'))
      .finally(() => setLoading(false))
  }, [role])

  useEffect(() => {
    let current = true
    if (!selectedId) {
      setWorkspace(EMPTY_WORKSPACE)
      setWorkspaceOwner('')
      setWorkspaceLoading(false)
      return () => { current = false }
    }
    setWorkspace(EMPTY_WORKSPACE)
    setWorkspaceOwner('')
    setWorkspaceLoading(true)
    service.loadWorkspace(selectedId).then((value) => {
      if (current) {
        setWorkspace(value)
        setWorkspaceOwner(selectedId)
      }
    }).catch(() => {
      if (current) {
        setWorkspace({ ...EMPTY_WORKSPACE, gstUnavailable: true })
        setWorkspaceOwner(selectedId)
        setError('Compliance details are temporarily unavailable.')
      }
    }).finally(() => {
      if (current) setWorkspaceLoading(false)
    })
    return () => { current = false }
  }, [selectedId, service])

  if (!role) return <Navigate to="/403" replace />
  if (loading) return <div className="card card-elevated p-8 text-text-secondary">Loading your business workspace...</div>

  const selected = context?.entities.find((item) => item.id === selectedId)
  const isOwner = role === 'business_owner_client'
  const pending = grants.filter((item) => item.status === 'pending')
  const outstandingFees = workspace.payments.filter((item) => item.status !== 'completed')
    .reduce((sum, item) => sum + (item.amount || 0), 0)

  async function submitGrant(event: FormEvent) {
    event.preventDefault()
    if (!selectedId || !grantee.trim() || !evidence.trim()) return
    setNotice('')
    try {
      await service.createGrant({
        customerId: selectedId, granteeUserId: grantee.trim(),
        actions: DEFAULT_ACTIONS, expiresAt: expiry ? new Date(expiry).toISOString() : undefined,
        evidenceReference: evidence.trim(),
      })
      setGrantee(''); setEvidence(''); setExpiry('')
      setNotice('Representative invitation created. It remains inactive until accepted.')
      await refresh()
    } catch {
      setError('The representative invitation could not be created.')
    }
  }

  async function decide(grantId: string, decision: 'accept' | 'decline') {
    await service.decideGrant(grantId, decision)
    setNotice(decision === 'accept'
      ? 'Authorization accepted. Refreshed access applies within the session security window.'
      : 'Authorization declined.')
    await refresh()
  }

  async function revoke(grantId: string) {
    await service.revokeGrant(grantId)
    setNotice('Representative access revoked. Existing access tokens expire within 30 minutes.')
    await refresh()
  }

  return (
    <div className="space-y-6">
      <header className="relative overflow-hidden rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-teal-50 p-6 shadow-sm dark:border-amber-900/50 dark:from-amber-950/30 dark:via-surface-dark dark:to-teal-950/30 sm:p-8">
        <div className="absolute -right-12 -top-12 h-40 w-40 rounded-full bg-amber-300/20 blur-2xl" />
        <div className="relative flex flex-wrap items-start justify-between gap-5">
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em] text-amber-700 dark:text-amber-300"><Landmark size={18} /> TaxHive business desk</div>
            <h1 className="text-3xl font-black tracking-tight text-text-primary sm:text-4xl">My Business</h1>
            <p className="max-w-2xl text-text-secondary">One secure view of every business entity you own or are explicitly authorized to represent.</p>
          </div>
          <div className="rounded-2xl border border-teal-200 bg-white/80 px-4 py-3 text-sm dark:border-teal-900 dark:bg-surface-dark/80">
            <div className="flex items-center gap-2 font-semibold text-teal-800 dark:text-teal-200"><ShieldCheck size={18} /> {isOwner ? 'Owner membership' : 'Scoped representative'}</div>
            <p className="mt-1 text-text-secondary">Entity and action boundaries are enforced server-side.</p>
          </div>
        </div>
      </header>

      {(error || notice) && <div role="status" className={`rounded-xl border p-4 text-sm ${error ? 'border-red-300 bg-red-50 text-red-800' : 'border-teal-300 bg-teal-50 text-teal-800'}`}>{error || notice}</div>}

      <section className="space-y-3" aria-label="Business entity switcher">
        <div className="flex items-center gap-2"><Building2 size={20} /><h2 className="text-lg font-bold text-text-primary">Your entities</h2></div>
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {context?.entities.map((item) => (
            <button key={item.id} type="button" onClick={() => setSelectedId(item.id)}
              className={`rounded-2xl border p-4 text-left transition ${selectedId === item.id ? 'border-teal-500 bg-teal-50 shadow-sm dark:bg-teal-950/30' : 'border-border bg-surface-light hover:border-teal-300 dark:bg-surface-dark'}`}>
              <span className="block font-bold text-text-primary">{item.name}</span>
              <span className="mt-1 block text-sm text-text-secondary">GSTIN {item.gstin}</span>
              <span className="mt-2 inline-block rounded-full bg-black/5 px-2 py-1 text-xs font-semibold dark:bg-white/10">{item.accessKind === 'membership' ? 'Owned / member' : 'Authorized access'}</span>
            </button>
          ))}
          {!context?.entities.length && <div className="rounded-2xl border border-dashed border-border p-6 text-text-secondary">No active business relationship is linked to this account.</div>}
        </div>
      </section>

      {selected && (
        <>
          <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {[
              { label: 'GST filings', value: visibleWorkspaceLoading ? '…' : workspace.gstUnavailable ? 'Unavailable' : workspace.gst.length, icon: FileCheck2 },
              { label: 'Upcoming deadlines', value: workspace.deadlines.length, icon: CalendarDays },
              { label: 'Open notices', value: workspace.notices.length, icon: ShieldCheck },
              { label: 'Outstanding fees', value: currency(outstandingFees), icon: IndianRupee },
            ].map(({ label, value, icon: Icon }) => (
              <div key={label} className="card card-elevated p-5"><Icon className="mb-3 text-teal-600" size={22} /><p className="text-sm text-text-secondary">{label}</p><p className="mt-1 text-2xl font-black text-text-primary">{value}</p></div>
            ))}
          </section>

          <section className="card card-elevated p-5">
            <h2 className="text-xl font-bold text-text-primary">{selected.name}</h2>
            <div className="mt-3 grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
              <div><span className="text-text-secondary">PAN</span><p className="font-semibold">{selected.pan}</p></div>
              <div><span className="text-text-secondary">GSTIN</span><p className="font-semibold">{selected.gstin}</p></div>
              <div><span className="text-text-secondary">TAN</span><p className="font-semibold">{selected.tan || 'Not recorded'}</p></div>
              <div><span className="text-text-secondary">Entity type</span><p className="font-semibold">{pretty(selected.businessType)}</p></div>
            </div>
          </section>

          {visibleWorkspaceLoading ? <div className="card card-elevated p-6 text-text-secondary">Loading entity compliance...</div> : (
            <div className="grid gap-5 xl:grid-cols-2">
              <Section title="GST compliance" hint="Returns and filing periods for this GST registration." items={workspace.gst} unavailable={workspace.gstUnavailable} />
              <Section title="Income tax" hint="Business ITR preparation and filing status." items={workspace.incomeTax} />
              <Section title="TDS" hint="TAN-linked deadlines surfaced from your compliance calendar." items={workspace.tds} />
              <Section title="Notices and queries" hint="Visible information requests, notices, and response dates." items={workspace.notices} />
              <Section title="Document vault" hint="Client-visible documents linked to this entity." items={workspace.documents} />
              <Section title="Deadlines" hint="Upcoming client-visible compliance reminders." items={workspace.deadlines} />
              <Section title="Fees and payments" hint="Entity-scoped professional fees and payment history." items={workspace.payments} />
            </div>
          )}
        </>
      )}

      {isOwner ? (
        <section className="card card-elevated p-5 space-y-5">
          <div className="flex items-center gap-2"><UserRoundCog size={21} /><div><h2 className="text-lg font-bold text-text-primary">Representative access</h2><p className="text-sm text-text-secondary">Invite a known user ID with explicit actions and an optional expiry.</p></div></div>
          <form onSubmit={submitGrant} className="grid gap-3 lg:grid-cols-4">
            <label className="text-sm font-medium text-text-primary">Representative user ID<input aria-label="Representative user ID" value={grantee} onChange={(event) => setGrantee(event.target.value)} required className="input mt-1 w-full" /></label>
            <label className="text-sm font-medium text-text-primary">Evidence reference<input aria-label="Evidence reference" value={evidence} onChange={(event) => setEvidence(event.target.value)} required className="input mt-1 w-full" /></label>
            <label className="text-sm font-medium text-text-primary">Access expiry<input aria-label="Access expiry" type="datetime-local" value={expiry} onChange={(event) => setExpiry(event.target.value)} className="input mt-1 w-full" /></label>
            <button type="submit" className="btn btn-primary self-end">Invite representative</button>
          </form>
          <div className="space-y-3">
            {grants.map((item) => (
              <div key={item.id} className="rounded-xl border border-border p-4 sm:flex sm:items-center sm:justify-between">
                <div><p className="font-semibold text-text-primary">{item.granteeUserId}</p><p className="text-sm text-text-secondary">{item.actions.map(pretty).join(', ')}</p><p className="mt-1 text-xs text-text-secondary">{pretty(item.status)}{item.expiresAt ? ` / expires ${new Date(item.expiresAt).toLocaleDateString('en-IN')}` : ''}</p></div>
                {!['revoked', 'declined'].includes(item.status) && <button type="button" onClick={() => revoke(item.id)} className="btn btn-secondary mt-3 sm:mt-0">Revoke access</button>}
              </div>
            ))}
          </div>
        </section>
      ) : (
        <section className="card card-elevated p-5 space-y-4">
          <div><h2 className="text-lg font-bold text-text-primary">Authorization grants</h2><p className="text-sm text-text-secondary">Review who granted access, exactly what it permits, and when it expires.</p></div>
          {pending.map((item) => (
            <article key={item.id} className="rounded-xl border border-amber-300 bg-amber-50/60 p-4 dark:bg-amber-950/20">
              <h3 className="font-bold text-text-primary">Pending authorization for entity {item.customerId}</h3>
              <p className="mt-1 text-sm text-text-secondary">Granted by {item.grantorUserId} / {item.evidenceReference}</p>
              <p className="mt-2 text-sm text-text-primary">{item.actions.map(pretty).join(', ')}</p>
              <div className="mt-4 flex gap-2"><button type="button" onClick={() => decide(item.id, 'accept')} className="btn btn-primary">Accept authorization</button><button type="button" onClick={() => decide(item.id, 'decline')} className="btn btn-secondary">Decline</button></div>
            </article>
          ))}
          {pending.length === 0 && <p className="rounded-xl border border-dashed border-border p-4 text-sm text-text-secondary">No authorization invitation needs your response.</p>}
        </section>
      )}
    </div>
  )
}
